import axios, { type InternalAxiosRequestConfig } from 'axios'
import { redirectToLogin } from '@/utils'
import { API_PATHS } from '@/constants'

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

type QueueEntry = {
  resolve: () => void
  reject: (reason: unknown) => void
}

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
})

const SKIP_REFRESH_URLS = new Set<string>([API_PATHS.AUTH.ME, API_PATHS.AUTH.REFRESH])

let isRefreshing = false
let pendingQueue: QueueEntry[] = []

function drainQueue(error: unknown): void {
  for (const entry of pendingQueue) {
    if (error) {
      entry.reject(error)
    } else {
      entry.resolve()
    }
  }
  pendingQueue = []
}

function waitForRefresh(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    pendingQueue.push({ resolve, reject })
  })
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config as RetryableRequestConfig
    const isUnauthorized = error.response?.status === 401
    const requestUrl = config?.url ?? ''
    const shouldSkip = SKIP_REFRESH_URLS.has(requestUrl)

    if (isUnauthorized && !shouldSkip && !config._retry) {
      if (isRefreshing) {
        try {
          await waitForRefresh()
          config._retry = true
          return apiClient(config)
        } catch {
          return Promise.reject(error)
        }
      }

      config._retry = true
      isRefreshing = true

      try {
        await apiClient.post(API_PATHS.AUTH.REFRESH)
        drainQueue(null)
        return apiClient(config)
      } catch (refreshError) {
        drainQueue(refreshError)
        redirectToLogin()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    if (isUnauthorized && shouldSkip && requestUrl !== API_PATHS.AUTH.ME) {
      redirectToLogin()
    }

    return Promise.reject(error)
  },
)

export default apiClient
